import { buildQueryParameters, getPopup, QueryParameter } from "./common";
import HttpClient from "./http-client";
import { PopupOptions } from "./popup";
import ProductCount from "./product-count";
import { TronLogoEvent } from "./tron-logo-event";

enum CalculatorActions {
    Init = "INIT",
    Edit = "EDIT"
}

const resourceTypeTronLogoLogo = 11204;

export class CalculatorOptions {
    action = CalculatorActions.Init;
    parentId: number;
    tronLogoFileGuid: string;
    tronLogoFileType: number;
    productCounts: ProductCount[];
    modelGuid: string;
    positionGuid: string;
    technologyGuid: string;
    calculationGuid: string;
    supplierGuid: string;
    dimension1: string;
    dimension2: string;
}

interface CalculatorConfig {
    calculatorUrl: string;
    languageCode: string;
    currencyCode: string;
    trackingId: string;
    shopGuid: string;
    returnUrl: string;
    customerSubjectGuid: string;
}

export default class TronCalculator {

    private config: CalculatorConfig;

    async openExistingCalculation(calculationGuid: string): Promise<void> {
        await this.openCalculator({ calculationGuid: calculationGuid, action: CalculatorActions.Edit } as CalculatorOptions);
    }

    async openFromMatrix(parentId: number, productModelGuid: string, supplierGuid: string, productCounts: ProductCount[], technologyGuid: string = null, positionGuid: string = null) {
        await this.openCalculator({
            parentId: parentId,
            modelGuid: productModelGuid,
            supplierGuid: supplierGuid,
            productCounts: productCounts,
            positionGuid: positionGuid,
            technologyGuid: technologyGuid
        } as CalculatorOptions);
    }

    async openForProduct(parentId: number, productModelGuid: string, supplierGuid: string, dimension1: string, dimension2: string): Promise<void> {
        await this.openCalculator({
            parentId: parentId,
            modelGuid: productModelGuid,
            supplierGuid: supplierGuid,
            dimension1: dimension1,
            dimension2: dimension2
        } as CalculatorOptions);
    }

    private async openCalculator(options: CalculatorOptions): Promise<void> {
        await this.loadConfig();
        if (!options.tronLogoFileGuid && options.parentId) {
            const tronLogoEvent = await this.loadTronLogoFileGuid(options.parentId);
            options.tronLogoFileGuid = tronLogoEvent?.motiveId ?? tronLogoEvent?.visualId;
            options.tronLogoFileType = tronLogoEvent?.resourceType;
        }
        const url = this.getUrl(options);
        this.openIFrame(url);
    }

    private async loadConfig(): Promise<void> {
        if (this.config) {
            return;
        }

        const response = await new HttpClient().fetch(`/api/calculation/settings`);
        this.config = await response.json();
    }

    private openIFrame(url: string): void {
        getPopup({ id: 'tron-calculator-popup' } as PopupOptions).open(`<iframe src="${url}" scrolling="auto" style="overflow:hidden;overflow-x:hidden;overflow-y:hidden;height:100%;width:100%;position:absolute;top:0px;left:0px;right:0px;bottom:0px" height="100%" width="100%" frameborder="0"></iframe>`);

    }

    private async loadTronLogoFileGuid(parentId: number): Promise<TronLogoEvent> {
        const url = `/api/log/last-motive?productId=${parentId}`;
        const response = await new HttpClient().fetch(url, null, (e) => { });
        const event = response.ok ? (await response.json() as TronLogoEvent) : null;
        if (event) return event;
        const currentLogo = window.tron?.User?.getUserLogo() ?? null;
        return currentLogo ? { motiveId: currentLogo, resourceType: resourceTypeTronLogoLogo } as TronLogoEvent : null;
    }

    private getUrl(options: CalculatorOptions): string {
        const queryParams = this.buildQueryParameters(options);
        return this.config.calculatorUrl + '&' + buildQueryParameters(queryParams);
    }

    private buildQueryParameters(options: CalculatorOptions): QueryParameter[] {
        const params = [
            { key: "p", value: (options.action ?? CalculatorActions.Init).toString() },
            { key: "l", value: this.config.languageCode?.toLowerCase() },
            { key: "c", value: this.config.currencyCode?.toLowerCase() },
            { key: "shg", value: this.config.shopGuid },
            { key: "cstg", value: this.config.customerSubjectGuid },

            { key: "pg", value: options.modelGuid },
            { key: "spg", value: options.supplierGuid },

            { key: "tracking", value: this.config.trackingId },
            { key: "url", value: encodeURI(this.config.returnUrl) },
        ];

        options.dimension1 && params.push({ key: "pd1", value: options.dimension1 });
        options.dimension2 && params.push({ key: "pd2", value: options.dimension2 });

        options.positionGuid && params.push({ key: "posg", value: options.positionGuid });
        options.technologyGuid && params.push({ key: "techg", value: options.technologyGuid });

        options.calculationGuid && params.push({ key: "cg", value: options.calculationGuid });

        options.tronLogoFileGuid && params.push({ key: "tlg", value: options.tronLogoFileGuid });
        options.tronLogoFileType && params.push({ key: "tlt", value: options.tronLogoFileType.toString() });

        if (options.productCounts) {
            const products = options.productCounts
                .map(c => `${c.productGuid}::${c.quantity}`)
                .join('||');

            params.push({ key: "products", value: products });
        }

        return params;
    }
}
